/**
 * Calendar Component
 * Month view calendar for selecting booking dates — Cal.com style
 */

import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CalendarProps {
    onDateSelect: (date: Date) => void;
    selectedDate?: Date;
    availableDates?: string[]; // Array of YYYY-MM-DD strings
    minDate?: Date;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const formatLocalDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

export function Calendar({ onDateSelect, selectedDate, availableDates = [], minDate }: CalendarProps) {
    const [currentMonth, setCurrentMonth] = useState(() => {
        const d = new Date();
        d.setDate(1);
        return d;
    });

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const startingDayOfWeek = firstDayOfMonth.getDay();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const calendarDays: (Date | null)[] = [];
    for (let i = 0; i < startingDayOfWeek; i++) calendarDays.push(null);
    for (let day = 1; day <= daysInMonth; day++) calendarDays.push(new Date(year, month, day));

    const isPast = (date: Date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        const min = minDate ? new Date(minDate) : today;
        min.setHours(0, 0, 0, 0);
        return d < min;
    };

    const isAvailable = (date: Date) => {
        if (availableDates.length === 0) return true;
        return availableDates.includes(formatLocalDate(date));
    };

    const isSelected = (date: Date) => {
        if (!selectedDate) return false;
        return formatLocalDate(date) === formatLocalDate(selectedDate);
    };

    const isToday = (date: Date) => formatLocalDate(date) === formatLocalDate(today);

    const canGoPrev = () => {
        const prevMonth = new Date(year, month - 1, 1);
        const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return prevMonth >= thisMonth;
    };

    return (
        <div className="w-full select-none">
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-6">
                <button
                    onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}
                    disabled={!canGoPrev()}
                    className={cn(
                        'p-1.5 rounded-md transition-colors',
                        canGoPrev()
                            ? 'hover:bg-accent text-foreground'
                            : 'text-muted-foreground/30 cursor-not-allowed'
                    )}
                >
                    <ChevronLeft className="h-5 w-5" />
                </button>

                <span className="text-base font-semibold tracking-wide">
                    {MONTHS[month]} {year}
                </span>

                <button
                    onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}
                    className="p-1.5 rounded-md hover:bg-accent transition-colors text-foreground"
                >
                    <ChevronRight className="h-5 w-5" />
                </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 mb-2">
                {DAYS.map((day) => (
                    <div
                        key={day}
                        className="text-center text-xs font-medium text-muted-foreground py-1 uppercase tracking-wider"
                    >
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-y-1">
                {calendarDays.map((date, index) => {
                    if (!date) return <div key={index} />;

                    const past = isPast(date);
                    const available = isAvailable(date);
                    const disabled = past || !available;
                    const selected = isSelected(date);
                    const todayDate = isToday(date);

                    return (
                        <div key={index} className="flex items-center justify-center">
                            <button
                                onClick={() => !disabled && onDateSelect(date)}
                                disabled={disabled}
                                className={cn(
                                    'relative w-10 h-10 rounded-full text-sm font-medium transition-all duration-150',
                                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                    // Disabled / no slot
                                    disabled && 'text-muted-foreground/40 cursor-not-allowed',
                                    // Available but not selected
                                    !disabled && !selected && [
                                        'text-foreground font-semibold',
                                        'hover:bg-accent hover:text-accent-foreground',
                                    ],
                                    // Selected
                                    selected && 'bg-primary text-primary-foreground shadow-sm',
                                )}
                            >
                                {date.getDate()}
                                {/* Dot for today */}
                                {todayDate && !selected && (
                                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
